import { useState, useEffect, useCallback } from "react"
import { Container, Row, Col, Spinner, Card, Table, Badge, Form, InputGroup } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import API from "../../common/API"
import { parsedUser } from "../../common/GetCurrentUser"
import { isSuperAdmin } from "../../common/helpers"
import DateRangeFilter from "../../components/indicators/DateRangeFilter"
import IndicatorCard from "../../components/indicators/IndicatorCard"
import SuperAdminBackLink from "../../components/platform/SuperAdminBackLink"
import Pagination from "../../components/core/Pagination"
import moment from "moment"
import {
  DispatchSummary,
  DispatchSpeed,
  VehicleVisitStats,
  DispatchDetailProduct,
  DispatchDetailCategory,
  DispatchDetailSeller,
  DispatchDetailClient,
  DispatchMonthlyTrend,
  DispatchIntelligence,
} from "../../types/Indicators.types"
import { Bar, Line } from "react-chartjs-2"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend)

const CATEGORY_COLORS = [
  "#0d6efd", "#198754", "#fd7e14", "#6f42c1", "#dc3545",
  "#0dcaf0", "#ffc107", "#20c997", "#d63384", "#6c757d",
]

interface OrganizationOption {
  id: string
  name: string
}

interface DispatchIndicatorsPageProps {
  embedded?: boolean
}

export default function DispatchIndicatorsPage({ embedded = false }: DispatchIndicatorsPageProps) {
  const { t } = useTranslation("global")
  const user: any = parsedUser()
  const userOrgId = user?.OrganizationId || user?.organizationId || ""
  const canSelectOrganization = isSuperAdmin(user)

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingOrganizations, setIsLoadingOrganizations] = useState(false)
  const [organizations, setOrganizations] = useState<OrganizationOption[]>([])
  const [selectedOrganizationId, setSelectedOrganizationId] = useState(canSelectOrganization ? "" : userOrgId)
  const [dispatch, setDispatch] = useState<DispatchSummary | null>(null)
  const [intelligence, setIntelligence] = useState<DispatchIntelligence | null>(null)
  const [speed, setSpeed] = useState<DispatchSpeed | null>(null)
  const [vehicleStats, setVehicleStats] = useState<VehicleVisitStats | null>(null)
  const [byProduct, setByProduct] = useState<DispatchDetailProduct[]>([])
  const [byCategory, setByCategory] = useState<DispatchDetailCategory[]>([])
  const [bySeller, setBySeller] = useState<DispatchDetailSeller[]>([])
  const [byClient, setByClient] = useState<DispatchDetailClient[]>([])
  const [monthlyTrend, setMonthlyTrend] = useState<DispatchMonthlyTrend[]>([])
  const [activeStartDate, setActiveStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"))
  const [activeEndDate, setActiveEndDate] = useState(moment().format("YYYY-MM-DD"))
  const [productStartDate, setProductStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"))
  const [productEndDate, setProductEndDate] = useState(moment().format("YYYY-MM-DD"))
  const [sellerPage, setSellerPage] = useState(0)
  const [clientPage, setClientPage] = useState(0)
  const [productPage, setProductPage] = useState(0)
  const [isLoadingProducts, setIsLoadingProducts] = useState(false)
  const [productSearch, setProductSearch] = useState("")
  const [selectedBrand, setSelectedBrand] = useState("")
  const effectiveOrgId = canSelectOrganization ? selectedOrganizationId : userOrgId
  const PAGE_SIZE = 10
  const PRODUCT_PAGE_SIZE = 12

  const clearData = useCallback(() => {
    setDispatch(null)
    setIntelligence(null)
    setSpeed(null)
    setVehicleStats(null)
    setByProduct([])
    setByCategory([])
    setBySeller([])
    setByClient([])
    setMonthlyTrend([])
    setSellerPage(0)
    setClientPage(0)
    setProductPage(0)
  }, [])

  const loadOrganizations = useCallback(async () => {
    if (!canSelectOrganization) return

    try {
      setIsLoadingOrganizations(true)
      const res = await API.getAction("Organization/AdminList", "Page=0&Quantity=200")
      if (res.data.status === "ok") {
        const records = res.data.response ?? []
        setOrganizations(records)
      }
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoadingOrganizations(false)
    }
  }, [canSelectOrganization])

  const loadTopProducts = useCallback(async (startDate: string, endDate: string) => {
    if (!effectiveOrgId) return

    try {
      setIsLoadingProducts(true)
      const params = `organizationId=${effectiveOrgId}&startDate=${startDate}&endDate=${endDate}`
      const res = await API.getAction("Indicators/DispatchDetailByProduct", params)

      if (res.data.status === "ok") {
        setByProduct(res.data.response ?? [])
        setProductStartDate(startDate)
        setProductEndDate(endDate)
      }
    } catch (ex) {
      console.error(ex)
      setByProduct([])
    } finally {
      setIsLoadingProducts(false)
    }
  }, [effectiveOrgId])

  const bindData = useCallback(async (startDate?: string, endDate?: string) => {
    if (!effectiveOrgId) return

    try {
      setIsLoading(true)
      const start = startDate || moment().startOf("month").format("YYYY-MM-DD")
      const end = endDate || moment().format("YYYY-MM-DD")
      setActiveStartDate(start)
      setActiveEndDate(end)
      const dateParams = `organizationId=${effectiveOrgId}&startDate=${start}&endDate=${end}`

      const [dispatchRes, intelligenceRes, speedRes, vehicleRes, productRes, categoryRes, sellerRes, clientRes, trendRes] =
        await Promise.all([
          API.getAction("Indicators/DispatchSummary", dateParams),
          API.getAction("Indicators/DispatchIntelligence", dateParams),
          API.getAction("Indicators/DispatchSpeed", dateParams),
          API.getAction("Indicators/VehicleVisitStats", dateParams),
          API.getAction("Indicators/DispatchDetailByProduct", dateParams),
          API.getAction("Indicators/DispatchDetailByCategory", dateParams),
          API.getAction("Indicators/DispatchDetailBySeller", dateParams),
          API.getAction("Indicators/DispatchDetailByClient", dateParams),
          API.getAction("Indicators/DispatchMonthlyTrend", dateParams),
        ])

      if (dispatchRes.data.status === "ok") setDispatch(dispatchRes.data.response)
      if (intelligenceRes.data.status === "ok") setIntelligence(intelligenceRes.data.response)
      if (speedRes.data.status === "ok") setSpeed(speedRes.data.response)
      if (vehicleRes.data.status === "ok") setVehicleStats(vehicleRes.data.response)
      if (productRes.data.status === "ok") {
        setByProduct(productRes.data.response ?? [])
        setProductStartDate(start)
        setProductEndDate(end)
      }
      if (categoryRes.data.status === "ok") setByCategory(categoryRes.data.response ?? [])
      if (sellerRes.data.status === "ok") setBySeller(sellerRes.data.response ?? [])
      if (clientRes.data.status === "ok") setByClient(clientRes.data.response ?? [])
      if (trendRes.data.status === "ok") setMonthlyTrend(trendRes.data.response ?? [])
    } catch (ex) {
      console.error(ex)
    } finally {
      setIsLoading(false)
    }
  }, [effectiveOrgId])

  useEffect(() => {
    setSellerPage(0)
  }, [bySeller])

  useEffect(() => {
    setClientPage(0)
  }, [byClient])

  useEffect(() => {
    setProductPage(0)
  }, [byProduct, productSearch, selectedBrand])

  useEffect(() => {
    loadOrganizations()
  }, [loadOrganizations])

  useEffect(() => {
    if (!effectiveOrgId) {
      clearData()
      return
    }

    bindData(activeStartDate, activeEndDate)
  }, [effectiveOrgId, bindData, clearData, activeStartDate, activeEndDate])

  const monthLabels = dispatch?.byMonth.map(m => `${m.year}-${String(m.month).padStart(2, "0")}`) ?? []

  const tripsChart = {
    labels: monthLabels,
    datasets: [{
      label: t("Trips"),
      data: dispatch?.byMonth.map(m => m.count) ?? [],
      backgroundColor: "#0d6efd",
      borderColor: "#0d6efd",
      borderRadius: 4,
    }],
  }

  const speedChart = {
    labels: speed?.byMonth.map(m => `${m.year}-${String(m.month).padStart(2, "0")}`) ?? [],
    datasets: [{
      label: t("Avg Days"),
      data: speed?.byMonth.map(m => m.avgDays) ?? [],
      borderColor: "#fd7e14",
      backgroundColor: "#fd7e1433",
      tension: 0.3,
      fill: true,
    }],
  }

  const trendLabels = monthlyTrend.map(m => `${m.year}-${String(m.month).padStart(2, "0")}`)
  const trendChart = {
    labels: trendLabels,
    datasets: [
      {
        label: t("Dispatches"),
        data: monthlyTrend.map(m => m.dispatchCount),
        borderColor: "#0d6efd",
        backgroundColor: "#0d6efd33",
        tension: 0.3,
        fill: false,
        yAxisID: "y",
      },
      {
        label: t("Total Qty"),
        data: monthlyTrend.map(m => m.totalQuantity),
        borderColor: "#198754",
        backgroundColor: "#19875433",
        tension: 0.3,
        fill: false,
        yAxisID: "y1",
      },
    ],
  }

  const categoryChart = {
    labels: byCategory.map(c => c.category),
    datasets: [{
      label: t("Qty"),
      data: byCategory.map(c => c.totalQuantity),
      backgroundColor: byCategory.map((_, i) => CATEGORY_COLORS[i % CATEGORY_COLORS.length]),
      borderRadius: 4,
    }],
  }

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${Math.round(seconds)}s`
    if (seconds < 3600) return `${Math.round(seconds / 60)}m`
    return `${(seconds / 3600).toFixed(1)}h`
  }

  const formatPercent = (value: number) => `${value.toFixed(1)}%`
  const pagedSellers = bySeller.slice(sellerPage * PAGE_SIZE, sellerPage * PAGE_SIZE + PAGE_SIZE)
  const pagedClients = byClient.slice(clientPage * PAGE_SIZE, clientPage * PAGE_SIZE + PAGE_SIZE)
  const sellerTotalPages = Math.ceil(bySeller.length / PAGE_SIZE)
  const clientTotalPages = Math.ceil(byClient.length / PAGE_SIZE)
  const availableBrands = [...new Set(byProduct.map(item => item.brand).filter(Boolean))].sort()
  const filteredProducts = byProduct.filter(item => {
    const search = productSearch.trim().toLowerCase()
    const matchesSearch = !search
      || item.itemCode?.toLowerCase().includes(search)
      || item.description?.toLowerCase().includes(search)
      || item.brand?.toLowerCase().includes(search)
    const matchesBrand = !selectedBrand || item.brand === selectedBrand
    return matchesSearch && matchesBrand
  })
  const pagedProducts = filteredProducts.slice(productPage * PRODUCT_PAGE_SIZE, productPage * PRODUCT_PAGE_SIZE + PRODUCT_PAGE_SIZE)
  const productTotalPages = Math.ceil(filteredProducts.length / PRODUCT_PAGE_SIZE)

  const chartOpts = { responsive: true, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }

  return (
    <Container fluid className="px-4 py-3">
      {!embedded && <SuperAdminBackLink />}

      <h4 className="mb-3">
        <i className="fas fa-truck me-2 text-primary" />
        {t("Indicators")} - {t("Dispatch")}
      </h4>

      {canSelectOrganization && (
        <Row className="g-2 align-items-end mb-3">
          <Col md={4} lg={3}>
            <Form.Label className="small text-muted mb-1">{t("Organization")}</Form.Label>
            <Form.Select
              size="sm"
              value={selectedOrganizationId}
              onChange={e => setSelectedOrganizationId(e.target.value)}
              disabled={isLoadingOrganizations}
            >
              {!selectedOrganizationId && <option value="">{t("Select Organization")}</option>}
              {organizations.map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </Form.Select>
          </Col>
        </Row>
      )}

      <DateRangeFilter onFilter={(s, e) => bindData(s, e)} loading={isLoading} />

      {canSelectOrganization && !selectedOrganizationId ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-4 text-center text-muted">
            {t("Select an organization to load dispatch indicators")}
          </Card.Body>
        </Card>
      ) : isLoading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          {/* ── KPI cards ── */}
          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Total Dispatches")} value={intelligence?.summary.totalDispatches ?? 0} format="number" icon="fa-truck-ramp-box" color="#0d6efd" />
            </Col>
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Total Quantity")} value={intelligence?.summary.totalQuantity ?? 0} format="number" icon="fa-boxes-stacked" color="#198754" />
            </Col>
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Portfolio Coverage")} value={formatPercent(intelligence?.portfolioCoverage.coveragePercent ?? 0)} icon="fa-bullseye" color="#fd7e14" />
            </Col>
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Dispatches With Trip")} value={intelligence?.logistics.dispatchesWithTrip ?? 0} format="number" icon="fa-route" color="#6f42c1" />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col md={4}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-chart-pie me-2 text-primary" />{t("Executive Summary")}</h6>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Total Lines")}</span>
                    <strong>{(intelligence?.summary.totalLines ?? 0).toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Distinct Clients")}</span>
                    <strong>{(intelligence?.summary.distinctClients ?? 0).toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Distinct Items")}</span>
                    <strong>{(intelligence?.summary.distinctItems ?? 0).toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Avg Lines per Dispatch")}</span>
                    <strong>{(intelligence?.summary.avgLinesPerDispatch ?? 0).toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between pt-2">
                    <span className="text-muted">{t("Avg Quantity per Dispatch")}</span>
                    <strong>{(intelligence?.summary.avgQuantityPerDispatch ?? 0).toLocaleString()}</strong>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-users me-2 text-success" />{t("Portfolio Coverage Health")}</h6>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Assigned Clients")}</span>
                    <strong>{(intelligence?.portfolioCoverage.assignedClients ?? 0).toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Dispatched Portfolio Clients")}</span>
                    <strong>{(intelligence?.portfolioCoverage.dispatchedPortfolioClients ?? 0).toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Coverage")}</span>
                    <Badge bg={(intelligence?.portfolioCoverage.coveragePercent ?? 0) >= 60 ? "success" : "warning"}>
                      {formatPercent(intelligence?.portfolioCoverage.coveragePercent ?? 0)}
                    </Badge>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Active Sellers")}</span>
                    <strong>{(intelligence?.portfolioCoverage.activeSellers ?? 0).toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between pt-2">
                    <span className="text-muted">{t("Seller Activation")}</span>
                    <Badge bg={(intelligence?.portfolioCoverage.sellerActivationPercent ?? 0) >= 60 ? "success" : "secondary"}>
                      {formatPercent(intelligence?.portfolioCoverage.sellerActivationPercent ?? 0)}
                    </Badge>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-heart-pulse me-2 text-danger" />{t("Operational Health")}</h6>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Dispatches Without Trip")}</span>
                    <strong>{(intelligence?.logistics.dispatchesWithoutTrip ?? 0).toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Trips Without Dispatch")}</span>
                    <strong>{(intelligence?.logistics.tripsWithoutDispatch ?? 0).toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Dispatches Without Invoice")}</span>
                    <strong>{(intelligence?.logistics.dispatchesWithoutInvoice ?? 0).toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Avg Days Trip to Dispatch")}</span>
                    <strong>{`${intelligence?.logistics.avgDaysTripToDispatch ?? 0} ${t("days")}`}</strong>
                  </div>
                  <div className="d-flex justify-content-between pt-2">
                    <span className="text-muted">{t("Avg Days Invoice to Dispatch")}</span>
                    <strong>{`${intelligence?.logistics.avgDaysInvoiceToDispatch ?? 0} ${t("days")}`}</strong>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col md={5}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-triangle-exclamation me-2 text-danger" />{t("Data Quality")}</h6>
                </Card.Header>
                <Card.Body>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Dispatches Without Details")}</span>
                    <strong>{(intelligence?.dataQuality.dispatchesWithoutDetails ?? 0).toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Details Without Dispatch")}</span>
                    <strong>{(intelligence?.dataQuality.detailsWithoutDispatch ?? 0).toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Details Without Seller")}</span>
                    <strong>{(intelligence?.dataQuality.detailsWithoutSeller ?? 0).toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between py-2 border-bottom">
                    <span className="text-muted">{t("Details Without Client")}</span>
                    <strong>{(intelligence?.dataQuality.detailsWithoutClient ?? 0).toLocaleString()}</strong>
                  </div>
                  <div className="d-flex justify-content-between pt-2">
                    <span className="text-muted">{t("Details Without Known Client")}</span>
                    <strong>{(intelligence?.dataQuality.detailsWithoutKnownClient ?? 0).toLocaleString()}</strong>
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={7}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-warehouse me-2 text-primary" />{t("Top Dependencies")}</h6>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">{t("Dependency")}</th>
                        <th className="text-end">{t("Dispatches")}</th>
                        <th className="text-end pe-3">{t("Total Qty")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(!intelligence?.topDependencies || intelligence.topDependencies.length === 0) && (
                        <tr><td colSpan={3} className="text-center text-muted py-3">{t("No data available")}</td></tr>
                      )}
                      {intelligence?.topDependencies.map((item, i) => (
                        <tr key={i}>
                          <td className="ps-3">{item.name}</td>
                          <td className="text-end">{item.dispatchCount.toLocaleString()}</td>
                          <td className="text-end pe-3">{item.totalQuantity.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xs={12}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-user-check me-2 text-primary" />{t("Seller Coverage")}</h6>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">{t("Seller")}</th>
                        <th className="text-end">{t("Assigned Clients")}</th>
                        <th className="text-end">{t("Dispatched Clients")}</th>
                        <th className="text-end">{t("Pending Clients")}</th>
                        <th className="text-end">{t("Coverage")}</th>
                        <th className="text-end">{t("Dispatches")}</th>
                        <th className="text-end pe-3">{t("Total Qty")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(!intelligence?.sellerCoverage || intelligence.sellerCoverage.length === 0) && (
                        <tr><td colSpan={7} className="text-center text-muted py-3">{t("No data available")}</td></tr>
                      )}
                      {intelligence?.sellerCoverage.map((item, i) => (
                        <tr key={i}>
                          <td className="ps-3">{item.sellerName}</td>
                          <td className="text-end">{item.assignedClients.toLocaleString()}</td>
                          <td className="text-end">{item.dispatchedClients.toLocaleString()}</td>
                          <td className="text-end">{item.undispachedClients.toLocaleString()}</td>
                          <td className="text-end">
                            <Badge bg={item.coveragePercent >= 60 ? "success" : item.coveragePercent >= 30 ? "warning" : "secondary"}>
                              {formatPercent(item.coveragePercent)}
                            </Badge>
                          </td>
                          <td className="text-end">{item.dispatchCount.toLocaleString()}</td>
                          <td className="text-end pe-3">{item.totalQuantity.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Total Trips")} value={dispatch?.totalTrips ?? 0} format="number" icon="fa-truck" color="#0d6efd" />
            </Col>
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Avg Dispatch Time")} value={`${speed?.avgDaysOverall ?? 0} ${t("days")}`} icon="fa-clock" color="#fd7e14" />
            </Col>
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Vehicle Visits")} value={vehicleStats?.totalVisits ?? 0} format="number" icon="fa-car" color="#198754" />
            </Col>
            <Col xs={6} md={3}>
              <IndicatorCard title={t("Visits > 5 min")} value={vehicleStats?.totalVisitsOver5Min ?? 0} format="number" icon="fa-stopwatch" color="#6f42c1" />
            </Col>
          </Row>

          {/* ── Monthly trend (dual axis: dispatches + qty) ── */}
          {monthlyTrend.length > 0 && (
            <Row className="g-3 mb-4">
              <Col xs={12}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white border-bottom">
                    <h6 className="mb-0"><i className="fas fa-chart-line me-2 text-primary" />{t("Monthly Dispatch Trend")}</h6>
                  </Card.Header>
                  <Card.Body>
                    <Line
                      data={trendChart}
                      options={{
                        responsive: true,
                        plugins: { legend: { display: true } },
                        scales: {
                          y:  { beginAtZero: true, position: "left",  title: { display: true, text: t("Dispatches") } },
                          y1: { beginAtZero: true, position: "right", title: { display: true, text: t("Quantity") }, grid: { drawOnChartArea: false } },
                        },
                      }}
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}

          {/* ── Trips by month + dispatch speed ── */}
          <Row className="g-3 mb-4">
            <Col md={6}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-chart-bar me-2 text-primary" />{t("Trips by Month")}</h6>
                </Card.Header>
                <Card.Body>
                  {monthLabels.length > 0
                    ? <Bar data={tripsChart} options={chartOpts} />
                    : <div className="text-center text-muted py-4">{t("No data available")}</div>}
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-shipping-fast me-2 text-warning" />{t("Avg Dispatch Time by Month")}</h6>
                </Card.Header>
                <Card.Body>
                  {speed && speed.byMonth.length > 0
                    ? <Line data={speedChart} options={chartOpts} />
                    : <div className="text-center text-muted py-4">{t("No data available")}</div>}
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── Category chart + category table ── */}
          <Row className="g-3 mb-4">
            <Col md={5}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-tags me-2 text-success" />{t("By Category")}</h6>
                </Card.Header>
                <Card.Body>
                  {byCategory.length > 0
                    ? <Bar data={categoryChart} options={{ ...chartOpts, indexAxis: "y" as const }} />
                    : <div className="text-center text-muted py-4">{t("No data available")}</div>}
                </Card.Body>
              </Card>
            </Col>
            <Col md={7}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-list me-2 text-success" />{t("Category Detail")}</h6>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">{t("Category")}</th>
                        <th className="text-end">{t("Total Qty")}</th>
                        <th className="text-end pe-3">{t("Dispatches")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byCategory.length === 0 && (
                        <tr><td colSpan={3} className="text-center text-muted py-3">{t("No data available")}</td></tr>
                      )}
                      {byCategory.map((c, i) => (
                        <tr key={i}>
                          <td className="ps-3">
                            <span className="me-2" style={{ color: CATEGORY_COLORS[i % CATEGORY_COLORS.length] }}>●</span>
                            {c.category}
                          </td>
                          <td className="text-end">{c.totalQuantity.toLocaleString()}</td>
                          <td className="text-end pe-3">
                            <Badge bg="secondary">{c.dispatchCount}</Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── By Seller + By Client ── */}
          <Row className="g-3 mb-4">
            <Col md={6}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-user-tie me-2 text-primary" />{t("By Seller — Qty Dispatched")}</h6>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">{t("Seller")}</th>
                        <th className="text-end">{t("Total Qty")}</th>
                        <th className="text-end pe-3">{t("Dispatches")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bySeller.length === 0 && (
                        <tr><td colSpan={3} className="text-center text-muted py-3">{t("No data available")}</td></tr>
                      )}
                      {pagedSellers.map((s, i) => (
                        <tr key={i}>
                          <td className="ps-3">{s.sellerName}</td>
                          <td className="text-end">{s.totalQuantity.toLocaleString()}</td>
                          <td className="text-end pe-3"><Badge bg="secondary">{s.dispatchCount}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
                {sellerTotalPages > 1 && (
                  <Card.Footer className="bg-white">
                    <Pagination
                      totalPages={sellerTotalPages}
                      currentPage={sellerPage}
                      onPageChanged={setSellerPage}
                      totalRecords={bySeller.length}
                    />
                  </Card.Footer>
                )}
              </Card>
            </Col>
            <Col md={6}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-building me-2 text-primary" />{t("By Client — Qty Dispatched")}</h6>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">{t("Client")}</th>
                        <th className="text-end">{t("Total Qty")}</th>
                        <th className="text-end pe-3">{t("Dispatches")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byClient.length === 0 && (
                        <tr><td colSpan={3} className="text-center text-muted py-3">{t("No data available")}</td></tr>
                      )}
                      {pagedClients.map((c, i) => (
                        <tr key={i}>
                          <td className="ps-3">{c.clientName}</td>
                          <td className="text-end">{c.totalQuantity.toLocaleString()}</td>
                          <td className="text-end pe-3"><Badge bg="secondary">{c.dispatchCount}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
                {clientTotalPages > 1 && (
                  <Card.Footer className="bg-white">
                    <Pagination
                      totalPages={clientTotalPages}
                      currentPage={clientPage}
                      onPageChanged={setClientPage}
                      totalRecords={byClient.length}
                    />
                  </Card.Footer>
                )}
              </Card>
            </Col>
          </Row>

          {/* ── Top products ── */}
          <Row className="g-3 mb-4">
            <Col xs={12}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-boxes me-2 text-warning" />{t("Top Dispatched Products")}</h6>
                </Card.Header>
                <Card.Body className="border-bottom">
                  <Row className="g-2">
                    <Col md={6}>
                      <InputGroup size="sm">
                        <InputGroup.Text><i className="fas fa-search" /></InputGroup.Text>
                        <Form.Control
                          placeholder={t("Search products")}
                          value={productSearch}
                          onChange={e => setProductSearch(e.target.value)}
                        />
                      </InputGroup>
                    </Col>
                    <Col md={4}>
                      <Form.Select
                        size="sm"
                        value={selectedBrand}
                        onChange={e => setSelectedBrand(e.target.value)}
                        disabled={isLoadingProducts}
                      >
                        <option value="">{t("All Brands")}</option>
                        {availableBrands.map(brand => (
                          <option key={brand} value={brand}>{brand}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={2}>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={productStartDate}
                        onChange={e => setProductStartDate(e.target.value)}
                        disabled={isLoadingProducts}
                        aria-label={t("Start Date")}
                      />
                    </Col>
                    <Col md={2}>
                      <Form.Control
                        type="date"
                        size="sm"
                        value={productEndDate}
                        onChange={e => setProductEndDate(e.target.value)}
                        disabled={isLoadingProducts}
                        aria-label={t("End Date")}
                      />
                    </Col>
                    <Col md={2}>
                      <button
                        type="button"
                        className="btn btn-sm btn-primary w-100"
                        onClick={() => loadTopProducts(productStartDate, productEndDate)}
                        disabled={isLoadingProducts || !productStartDate || !productEndDate}
                      >
                        {isLoadingProducts ? <i className="fas fa-spinner fa-spin me-1" /> : <i className="fas fa-filter me-1" />}
                        {t("Generate")}
                      </button>
                    </Col>
                  </Row>
                </Card.Body>
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">{t("Code")}</th>
                        <th>{t("Description")}</th>
                        <th>{t("Brand")}</th>
                        <th className="text-end">{t("Total Qty")}</th>
                        <th className="text-end pe-3">{t("Dispatches")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProducts.length === 0 && (
                        <tr><td colSpan={5} className="text-center text-muted py-3">{t("No data available")}</td></tr>
                      )}
                      {pagedProducts.map((p, i) => (
                        <tr key={i}>
                          <td className="ps-3 text-muted small">{p.itemCode}</td>
                          <td>{p.description}</td>
                          <td><Badge bg="light" text="dark">{p.brand || "—"}</Badge></td>
                          <td className="text-end fw-semibold">{p.totalQuantity.toLocaleString()}</td>
                          <td className="text-end pe-3"><Badge bg="secondary">{p.dispatchCount}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
                {productTotalPages > 1 && (
                  <Card.Footer className="bg-white">
                    <Pagination
                      totalPages={productTotalPages}
                      currentPage={productPage}
                      onPageChanged={setProductPage}
                      totalRecords={filteredProducts.length}
                    />
                  </Card.Footer>
                )}
              </Card>
            </Col>
          </Row>

          {/* ── By Vehicle + By Seller (trip count) ── */}
          <Row className="g-3 mb-4">
            <Col md={6}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-car me-2 text-primary" />{t("By Vehicle — Trip Count")}</h6>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">{t("Vehicle")}</th>
                        <th className="text-end pe-3">{t("Trips")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(!dispatch?.byVehicle || dispatch.byVehicle.length === 0) && (
                        <tr><td colSpan={2} className="text-center text-muted py-3">{t("No data available")}</td></tr>
                      )}
                      {dispatch?.byVehicle.map((v, i) => (
                        <tr key={i}>
                          <td className="ps-3">{v.name}</td>
                          <td className="text-end pe-3">{v.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
            <Col md={6}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white border-bottom">
                  <h6 className="mb-0"><i className="fas fa-user-tie me-2 text-primary" />{t("By Seller — Trip Count")}</h6>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">{t("Seller")}</th>
                        <th className="text-end pe-3">{t("Trips")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(!dispatch?.byVendedor || dispatch.byVendedor.length === 0) && (
                        <tr><td colSpan={2} className="text-center text-muted py-3">{t("No data available")}</td></tr>
                      )}
                      {dispatch?.byVendedor.map((v, i) => (
                        <tr key={i}>
                          <td className="ps-3">{v.name}</td>
                          <td className="text-end pe-3">{v.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* ── Vehicle visit details ── */}
          {vehicleStats && vehicleStats.perVehicle.length > 0 && (
            <Row className="g-3 mb-4">
              <Col xs={12}>
                <Card className="shadow-sm border-0">
                  <Card.Header className="bg-white border-bottom">
                    <h6 className="mb-0"><i className="fas fa-satellite-dish me-2 text-primary" />{t("Vehicle Visit Details")}</h6>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <Table hover responsive className="mb-0">
                      <thead className="table-light">
                        <tr>
                          <th className="ps-3">{t("Vehicle")}</th>
                          <th className="text-center">{t("Visits")}</th>
                          <th className="text-center">{t("Visits > 5 min")}</th>
                          <th className="text-center">{t("Avg Distance")}</th>
                          <th className="text-center">{t("Avg Duration")}</th>
                          <th className="text-center pe-3">{t("Sites Covered")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {vehicleStats.perVehicle.map((v, i) => (
                          <tr key={i}>
                            <td className="ps-3 fw-semibold">{v.vehicleName}</td>
                            <td className="text-center">{v.visitCount}</td>
                            <td className="text-center">{v.visitsOver5Min}</td>
                            <td className="text-center">{v.avgDistance.toFixed(1)} km</td>
                            <td className="text-center">{formatDuration(v.avgDurationSeconds)}</td>
                            <td className="text-center pe-3">{v.distinctSites}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  )
}
